/**
 * Individual requests (Legal → Individual requests, `docs/38`).
 *
 * What people asked about the messages Connect's clients send them: stop
 * promotions, stop everything, delete my details, let one business through.
 * Each comes from staff on the person's behalf, the website (with a code), or
 * the person's own STOP or unsubscribe — and every one is here, with what it
 * did.
 *
 * Oversight, not decisions (owner, 2026-10-23): each business decides about its
 * own customers in its own inbox. Staff verify who asked, route a request that
 * names a business to it, and chase a business that has not answered.
 *
 * **Requests** is the queue: *Needs action* first (to verify, to route, a
 * business past its date, to retry), search by reference, and the person found exactly by their number or
 * email. Ticked rows — or every row the filters match, the count confirmed —
 * take one action; the server checks each request on its own and says which it
 * did and why it did not do the rest.
 *
 * **Settings** is what the requests mean: the kinds of request, the
 * categories of message and each channel's words for them, and the business
 * types and groups a request may name.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, CircleSlash, Plus, RotateCcw, ShieldCheck, Undo2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  Input,
  Modal,
  PageHeader,
  ServerTable,
  Tabs,
  Textarea,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatNumber,
  toListParams,
  toast,
  useIdempotencyKey,
  type ServerColumn,
  type ServerListRequest,
} from '@connect/web-shared';

import { NewRequestDialog } from './new-request-dialog';
import {
  BASE,
  SOURCE,
  STATUS,
  requestKeys,
  requestsApi,
  type Action,
  type BulkResult,
  type RequestRow,
} from './requests-api';
import { RequestSettings } from './request-settings';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : undefined);

type Tab = 'requests' | 'settings';
type View = 'needs_action' | 'all';

export function IndividualRequestsPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'settings' ? 'settings' : 'requests';
  const [creating, setCreating] = useState(false);
  const counts = useQuery({ queryKey: requestKeys.counts, queryFn: requestsApi.counts });
  const settings = useQuery({ queryKey: requestKeys.settings, queryFn: requestsApi.settings });

  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title="Individual requests"
        description="What people asked about the messages Connect’s clients send them, where each went and what each business answered. Every send obeys them as it goes; each business decides about its own customers."
        breadcrumbs={[{ label: 'Legal' }, { label: 'Individual requests' }]}
        actions={
          <Can permission="messaging.individual_request.create">
            <Button disabled={!settings.data} onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              Record a request
            </Button>
          </Can>
        }
        tabs={
          <Tabs
            value={tab}
            onChange={(id) => setParams({ tab: id }, { replace: true })}
            items={[
              { id: 'requests', label: 'Requests', count: counts.data?.needs_action || undefined },
              { id: 'settings', label: 'Settings' },
            ]}
          />
        }
      />
      {tab === 'requests' ? <Queue /> : <RequestSettings />}
      {settings.data ? (
        <NewRequestDialog open={creating} onClose={() => setCreating(false)} settings={settings.data} />
      ) : null}
    </div>
  );
}

function Queue() {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('needs_action');
  const [person, setPerson] = useState('');
  const [looking, setLooking] = useState('');
  const extra = useMemo(
    () => ({ needs_action: view === 'needs_action' ? 'true' : undefined, person: looking || undefined }),
    [view, looking],
  );
  const fetchRows = useMemo(() => createServerFetcher<RequestRow>(BASE, extra), [extra]);
  const getFacets = useMemo(() => createFacetsFetcher(BASE, { needs_action: extra.needs_action }), [extra]);
  const columns = useMemo<ServerColumn<RequestRow>[]>(
    () => [
      {
        field: 'code',
        header: 'Reference',
        width: 140,
        render: (row) => <span className="font-mono text-xs text-heading">{row.code}</span>,
      },
      {
        field: 'type',
        header: 'What they asked',
        filter: 'select',
        minWidth: 190,
        render: (row) => (
          <span className="flex flex-wrap items-center gap-1.5">
            {row.type_label}
            {row.erasure ? <Badge tone="danger">Erasure</Badge> : null}
          </span>
        ),
      },
      {
        field: 'people',
        header: 'Person',
        sortable: false,
        minWidth: 170,
        render: (row) => <span className="font-mono text-xs">{row.people}</span>,
      },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        width: 140,
        render: (row) => (
          <span className="flex flex-col gap-0.5">
            <Badge tone={STATUS[row.status].tone}>{STATUS[row.status].label}</Badge>
            {row.status === 'failed' && row.last_error ? (
              <span className="text-2xs text-danger">{row.last_error}</span>
            ) : null}
          </span>
        ),
      },
      {
        field: 'source',
        header: 'Came from',
        filter: 'select',
        width: 170,
        render: (row) => SOURCE[row.source] ?? row.source,
      },
      {
        field: 'business_hint',
        header: 'Business they named',
        minWidth: 160,
        render: (row) => row.business_hint ?? <span className="text-faint">—</span>,
      },
      { field: 'created_at', header: 'Received', width: 120, format: (v) => formatDate(v as string) },
      {
        field: 'applied_at',
        header: 'Applied',
        width: 120,
        format: (v) => (v ? formatDate(v as string) : '—'),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Tabs
          value={view}
          onChange={(id) => setView(id as View)}
          items={[
            { id: 'needs_action', label: 'Needs action' },
            { id: 'all', label: 'All requests' },
          ]}
        />
        <form
          className="ml-auto flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setLooking(person.trim());
          }}
        >
          <Input
            size="sm"
            aria-label="Find a person"
            placeholder="Their exact mobile or email"
            value={person}
            onChange={(event) => setPerson(event.target.value)}
          />
          <Button size="sm" variant="secondary" type="submit">
            Find
          </Button>
          {looking ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setPerson('');
                setLooking('');
              }}
            >
              Clear
            </Button>
          ) : null}
        </form>
      </div>
      <ServerTable<RequestRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={[...requestKeys.list, extra]}
        stateKey="individual-requests"
        searchPlaceholder="Search by reference or business"
        viewField="code"
        onView={(row) => navigate(`/individual-requests/${row.public_id}`)}
        selectable
        bulkActions={(context) => <BulkBar {...context} extra={extra} />}
        emptyTitle={view === 'needs_action' ? 'Nothing waiting' : 'No requests yet'}
        emptyDescription={
          view === 'needs_action'
            ? 'Nothing waits on Connect: every request is routed, and no business is past its deadline.'
            : 'A request is recorded here when a person asks — by STOP, on the website, or through staff.'
        }
        fillHeight
      />
    </div>
  );
}

const ACTIONS: { id: Action; label: string; icon: typeof CheckCheck; note?: string; permission: string }[] = [
  { id: 'verify', label: 'Verify', icon: ShieldCheck, note: 'How you checked it is the person', permission: 'messaging.individual_request.edit' },
  { id: 'approve', label: 'Route', icon: CheckCheck, permission: 'messaging.individual_request.approve' },
  { id: 'reject', label: 'Reject', icon: CircleSlash, note: 'Why it is rejected', permission: 'messaging.individual_request.approve' },
  { id: 'withdraw', label: 'Withdraw', icon: Undo2, note: 'Why it is withdrawn', permission: 'messaging.individual_request.edit' },
  { id: 'retry', label: 'Retry', icon: RotateCcw, permission: 'messaging.individual_request.approve' },
];

function BulkBar({
  ids,
  allMatching,
  total,
  clear,
  request,
  extra,
}: {
  ids: string[];
  allMatching: boolean;
  total: number;
  clear: () => void;
  request: ServerListRequest;
  extra: Record<string, string | undefined>;
}) {
  const queryClient = useQueryClient();
  const keys = useIdempotencyKey();
  const [action, setAction] = useState<(typeof ACTIONS)[number] | null>(null);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<BulkResult | null>(null);
  const count = allMatching ? total : ids.length;
  const run = useMutation({
    mutationFn: () => {
      const body = allMatching
        ? { action: action!.id, matching: true, expected: total, note: note.trim() || undefined }
        : { action: action!.id, public_ids: ids, note: note.trim() || undefined };
      const params = allMatching ? { ...toListParams(request), ...extra } : { needs_action: extra.needs_action };
      return requestsApi.bulk(body, params, keys.keyFor({ body, params }));
    },
    onSuccess: async (done) => {
      keys.reset();
      setAction(null);
      setNote('');
      await queryClient.invalidateQueries({ queryKey: requestKeys.all });
      // The list of what was left lives in this bar, which clearing the
      // selection takes away: it is cleared once that list is read.
      if (done.refused.length) setResult(done);
      else {
        toast.success(`${formatNumber(done.done)} done`);
        clear();
      }
    },
  });
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {ACTIONS.map((item) => (
          <Can key={item.id} permission={item.permission}>
            <Button size="sm" variant="secondary" onClick={() => setAction(item)}>
              <item.icon className="size-4" />
              {item.label}
            </Button>
          </Can>
        ))}
      </div>
      <Modal
        open={action !== null}
        onClose={() => setAction(null)}
        title={action ? `${action.label} ${formatNumber(count)} request${count === 1 ? '' : 's'}?` : ''}
        description="Each request is checked on its own: one this does not fit is left as it is, and listed."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAction(null)}>
              Cancel
            </Button>
            <Button
              loading={run.isPending}
              disabled={Boolean(action?.note) && !note.trim()}
              onClick={() => run.mutate()}
            >
              {action?.label}
            </Button>
          </>
        }
      >
        {run.isError ? <Alert tone="danger">{errorText(run.error) ?? 'Nothing was done.'}</Alert> : null}
        {action?.note ? (
          <Textarea label={action.note} rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
        ) : null}
      </Modal>
      <Modal
        open={result !== null}
        onClose={() => {
          setResult(null);
          clear();
        }}
        title={`${formatNumber(result?.done ?? 0)} done, ${formatNumber(result?.refused.length ?? 0)} left as they were`}
        footer={
          <Button
            onClick={() => {
              setResult(null);
              clear();
            }}
          >
            Close
          </Button>
        }
      >
        <ul className="space-y-1.5 text-sm">
          {result?.refused.map((item) => (
            <li key={item.code}>
              <span className="font-mono text-xs text-heading">{item.code}</span> — {item.reason}
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}

